<script setup lang="ts">
import { dollars } from '@nuts/auto-delivery-sdk/dist/utils/money';
import dayjs from 'dayjs';
import { computed, inject } from 'vue';

import { ShipmentPickup, ShippingOffer } from '@/api/shippingCalculator';
import Skeleton from '@/components/base/Skeleton.vue';
import TextButton from '@/components/base/TextButton.vue';
import BaseBodyText from '@/components/base/typography/BaseBodyText.vue';
import Caption from '@/components/base/typography/Caption.vue';
import SmallBodyText from '@/components/base/typography/SmallBodyText.vue';
import money from '@/filters/money';
import { calculateDaysBetweenDates, DateString } from '@/utils/dateTime';

interface ShippingOfferSummary
  extends Partial<Pick<ShippingOffer, 'latestArrivalOn' | 'name' | 'price'>> {
  shipmentPickup: Partial<Pick<ShipmentPickup, 'carrierCode' | 'carrierPickup'>>;
}

const props = defineProps<{
  giftMessage?: boolean;
  highlight?: boolean;
  hasMultipleShipments?: boolean;
  isDigital?: boolean;
  isLoading?: boolean;
  isPickup?: boolean;
  oneClickCheckout?: boolean;
  shippingOffer: ShippingOfferSummary;
}>();

defineEmits<{ edit: [] }>();

const isAdmin = inject('isAdmin', false);

const calculateArrivalDay = (arrival: DateString): string => {
  let arrivalDay;
  const arrivalDate = dayjs(arrival, 'YYYY-MM-DD');
  const todaysDate = dayjs().format('YYYY-MM-DD') as DateString;
  const daysFromArrival = calculateDaysBetweenDates(todaysDate, arrival);

  switch (daysFromArrival) {
    case 0:
      arrivalDay = 'Today';
      break;
    case 1:
      arrivalDay = 'Tomorrow';
      break;
    default:
      arrivalDay = arrivalDate.format('dddd');
      break;
  }
  return `${arrivalDay}, ${arrivalDate.format('MMM D')}`;
};

const calculateDeliveryPrice = computed(() => {
  let price = 'FREE';

  if (
    !props.isPickup &&
    !props.isDigital &&
    props.shippingOffer.price &&
    dollars(props.shippingOffer.price) > 0
  ) {
    price = money(props.shippingOffer.price);
  }

  return price;
});
</script>

<template>
  <div class="flex flex-col">
    <div v-if="isLoading" class="flex flex-col w-full gap-y-1">
      <div class="flex justify-between">
        <Skeleton class="w-40 h-5 md:w-48 md:h-6" />
        <Skeleton class="w-10 h-5 md:w-12 md:h-6" />
      </div>
      <Skeleton class="h-3 md:h-4 w-44 md:w-52" />
    </div>
    <template v-else-if="oneClickCheckout">
      <div class="flex justify-between">
        <BaseBodyText>
          <template v-if="isDigital">Arrives Immediately</template>
          <template v-else>
            {{ isPickup ? 'Ready' : 'Arrives' }}
            <b class="text-nuts-lime-800">
              {{ calculateArrivalDay(shippingOffer.latestArrivalOn) }}
            </b>
          </template>
        </BaseBodyText>
        <BaseBodyText class="font-semibold">{{ calculateDeliveryPrice }}</BaseBodyText>
      </div>
      <SmallBodyText v-if="isDigital" class="text-neutral-500">Digital delivery</SmallBodyText>
      <TextButton v-else class="flex" @click="$emit('edit')">
        <SmallBodyText>View more shipping options</SmallBodyText>
      </TextButton>
      <div v-if="isAdmin" class="text-nuts-red-800">
        <BaseBodyText>
          {{ shippingOffer.shipmentPickup.carrierCode }}
        </BaseBodyText>
        <Caption class="text-xs">via {{ shippingOffer.shipmentPickup.carrierPickup }}</Caption>
      </div>
      <div v-if="!isDigital && giftMessage" :class="hasMultipleShipments ? 'mt-1' : 'mt-5'">
        <BaseBodyText>Free Gift Message Added</BaseBodyText>
        <div class="flex gap-1">
          <TextButton @click="$emit('edit')">
            <SmallBodyText>Edit gift message</SmallBodyText>
          </TextButton>
        </div>
      </div>
    </template>
    <template v-else>
      <BaseBodyText class="flex justify-between font-bold">
        <span>Delivery Options</span>
        <span>{{ calculateDeliveryPrice }}</span>
      </BaseBodyText>
      <div>
        <template v-if="isDigital">
          <BaseBodyText>Arrives Immediately</BaseBodyText>
          <BaseBodyText>Digital Delivery</BaseBodyText>
        </template>
        <template v-else-if="isPickup">
          <BaseBodyText>
            Ready {{ calculateArrivalDay(shippingOffer.latestArrivalOn) }}
          </BaseBodyText>
          <BaseBodyText>Customer Pickup</BaseBodyText>
        </template>
        <template v-else>
          <BaseBodyText class="text-nuts-lime-800">
            Arrives {{ calculateArrivalDay(shippingOffer.latestArrivalOn) }}
          </BaseBodyText>
          <BaseBodyText class="text-neutral-500">{{ shippingOffer.name }}</BaseBodyText>
          <div v-if="isAdmin" class="text-nuts-red-800">
            <BaseBodyText>
              {{ shippingOffer.shipmentPickup.carrierCode }}
            </BaseBodyText>
            <Caption class="text-xs">via {{ shippingOffer.shipmentPickup.carrierPickup }}</Caption>
          </div>
        </template>
      </div>
      <div v-if="giftMessage">
        <div class="flex justify-between mt-6 font-bold">
          <BaseBodyText>Gift Message</BaseBodyText>
          <BaseBodyText>FREE</BaseBodyText>
        </div>
        <BaseBodyText>Free Gift Message Added</BaseBodyText>
      </div>
    </template>
  </div>
</template>
